import { useCallback, useEffect, useRef, useState } from "react";
import {
  subscriptions as subscriptionsApi,
  type SubscriptionQueryParams,
  type SubscriptionsListWithPagination,
} from "../api/subscriptions.service.js";
import { crmWebhookLogs } from "../api/crm-webhook-logs.service.js";

const DEBOUNCE_MS = 300;

type UseSubscriptionsParams = Pick<
  SubscriptionQueryParams,
  "productCode" | "expiringInDays" | "sortField" | "sortDirection" | "fullyConfigured"
> & {
  search?: string;
  siteStructureId?: string;
  editorialCategoryId?: number;
  status: SubscriptionQueryParams["status"] | "all";
  page: number;
  pageSize: number;
};

type UseSubscriptionsResult = {
  data: SubscriptionsListWithPagination | null;
  loading: boolean;
  isSearching: boolean;
  error: Error | null;
  refetch: () => void;
  webhookFallbackCount: number | null;
  webhookFallbackSearch: string | null;
};

export function useSubscriptions({
  search,
  siteStructureId,
  editorialCategoryId,
  status,
  productCode,
  expiringInDays,
  fullyConfigured,
  sortField,
  sortDirection,
  page,
  pageSize,
}: UseSubscriptionsParams): UseSubscriptionsResult {
  const [data, setData] = useState<SubscriptionsListWithPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [webhookFallbackCount, setWebhookFallbackCount] = useState<number | null>(null);
  const [webhookFallbackSearch, setWebhookFallbackSearch] = useState<string | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFetchedOnce = useRef(false);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const fetchSubscriptions = useCallback(async () => {
    try {
      if (hasFetchedOnce.current) {
        setIsSearching(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params: SubscriptionQueryParams = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (siteStructureId) {
        params.siteStructureId = siteStructureId;
      }

      if (editorialCategoryId) {
        params.editorialCategoryId = editorialCategoryId;
      }

      if (status && status !== "all") {
        params.status = status;
      }

      if (productCode && productCode !== "all") {
        params.productCode = productCode;
      }

      if (expiringInDays !== undefined) {
        params.expiringInDays = expiringInDays;
      }

      if (fullyConfigured !== undefined) {
        params.fullyConfigured = fullyConfigured;
      }

      if (sortField) {
        params.sortField = sortField;
        params.sortDirection = sortDirection ?? "asc";
      }

      const result = await subscriptionsApi.getAll(params);
      setData(result);
      hasFetchedOnce.current = true;

      if (debouncedSearch && result.subscriptions.length === 0) {
        try {
          const webhookResult = await crmWebhookLogs.getAll({ search: debouncedSearch, limit: 1 });
          const totalWebhooks = webhookResult.total ?? webhookResult.logs.length;
          setWebhookFallbackCount(totalWebhooks > 0 ? totalWebhooks : null);
          setWebhookFallbackSearch(totalWebhooks > 0 ? debouncedSearch : null);
        } catch (webhookError) {
          setError(webhookError instanceof Error ? webhookError : new Error(String(webhookError)));
          setWebhookFallbackCount(null);
          setWebhookFallbackSearch(null);
        }
      } else {
        setWebhookFallbackCount(null);
        setWebhookFallbackSearch(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [
    debouncedSearch,
    siteStructureId,
    editorialCategoryId,
    status,
    productCode,
    expiringInDays,
    fullyConfigured,
    sortField,
    sortDirection,
    page,
    pageSize,
  ]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const refetch = useCallback(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  return {
    data,
    loading,
    isSearching,
    error,
    refetch,
    webhookFallbackCount,
    webhookFallbackSearch,
  };
}
